import { useEffect, useState } from 'react';

/**
 * Hook to track call duration in real-time
 *
 * Starts a timer when active becomes true and updates every second.
 * Returns formatted duration string (MM:SS format).
 *
 * @param isActive - Whether the timer should be running
 * @returns Formatted duration string (e.g., "05:23")
 */
export function useCallTimer(isActive: boolean): string {
  const [startTime, setStartTime] = useState<number | null>(null);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!isActive) {
      setStartTime(null);
      setDuration(0);
      return;
    }

    // Set start time when timer becomes active
    if (startTime === null) {
      setStartTime(Date.now());
    }

    const interval = setInterval(() => {
      if (startTime !== null) {
        setDuration(Date.now() - startTime);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, startTime]);

  return formatDuration(duration);
}

/**
 * Format duration in milliseconds to MM:SS string
 */
function formatDuration(durationMs: number): string {
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}
